import axios from 'axios';

import filesAPI from '@/api/files';
import axiosApiService from '@/services/axiosApiService';
import { WorkingProfileForm } from '@/components/EditProfilePage/schema';
import IFile from '@/types/IFile';
import { IProfileSettings } from '@/types/IProfile';

import { BASE_URL } from '../../config';

export interface OnboardingLocation {
  city: string | null;
  country: string | null;
}

export interface OnboardingCurrentPosition {
  title: string | null;
  companyName: string | null;
  companyLinkedInUrl: string | null;
}

export interface OnboardingPerson {
  firstName: string | null;
  lastName: string | null;
  headline: string | null;
  summary: string | null;
  photoUrl: string | null;
  linkedInUrl: string | null;
  location: OnboardingLocation | null;
  currentPosition: OnboardingCurrentPosition | null;
}

export interface OnboardingCompany {
  name: string | null;
  websiteUrl: string | null;
  linkedInUrl: string | null;
}

export interface OnboardingBranding {
  logoUrl: string | null;
  backgroundColorHex: string;
  accentColorHex: string | null;
  buttonTextColorHex: string;
  domain: string | null;
}

export interface OnboardingPositionChoice {
  title: string | null;
  companyName: string | null;
  companyLinkedInUrl: string | null;
  logoUrl: string | null;
}

export interface OnboardingProfileData {
  person: OnboardingPerson;
  company: OnboardingCompany | null;
}

export interface BrandingResult {
  coverImage: IFile | null;
  accentColorHex: string | null;
  buttonTextColor: string;
  domain: string | null;
  logoUrl: string | null;
}

const COVER_W = 1024;
const COVER_H = 704;
const MAX_LOGO_W = 820;
const MAX_LOGO_H = 560;

export interface EnrichProfileRequest {
  linkedInUrl: string;
}

export interface EnrichCompanyRequest {
  companyLinkedInUrl: string;
  preview_token: string;
}

export interface PendingCompanySelectionResponse {
  status: 'pending_company_selection';
  preview_token: string;
  person: OnboardingPerson;
  company: null;
  branding: null;
  positions: OnboardingPositionChoice[];
}

export interface CompleteProfileResponse {
  status: 'complete';
  preview_token: string;
  person: OnboardingPerson;
  company: OnboardingCompany | null;
  branding: OnboardingBranding | null;
  positions: [];
}

export type ProfileEnrichmentResponse =
  | PendingCompanySelectionResponse
  | CompleteProfileResponse;

export interface CompanyEnrichmentResponse {
  company: OnboardingCompany | null;
  branding: OnboardingBranding | null;
}

export async function enrichProfile(
  linkedInUrl: string,
): Promise<ProfileEnrichmentResponse> {
  const { data } = await axiosApiService.post<ProfileEnrichmentResponse>(
    `${BASE_URL}/api/onboarding/enrich/profile`,
    { linkedInUrl } satisfies EnrichProfileRequest,
  );

  return data;
}

export async function enrichCompany(
  companyLinkedInUrl: string,
  previewToken: string,
): Promise<CompanyEnrichmentResponse> {
  const { data } = await axiosApiService.post<CompanyEnrichmentResponse>(
    `${BASE_URL}/api/onboarding/enrich/company`,
    {
      companyLinkedInUrl,
      preview_token: previewToken,
    } satisfies EnrichCompanyRequest,
  );

  return data;
}

function buildSyntheticImageFile(name: string, imageUrl: string): IFile {
  return {
    id: 0,
    name,
    original_url: imageUrl,
    large_url: imageUrl,
    medium_url: imageUrl,
    small_url: imageUrl,
    thumb_url: imageUrl,
    blur_url: imageUrl,
    upload_url: '',
  };
}

async function generateCoverDataUrl(
  logoUrl: string,
  backgroundColorHex: string,
): Promise<string | null> {
  return await new Promise<string | null>((resolve) => {
    const canvas = document.createElement('canvas');
    canvas.width = COVER_W;
    canvas.height = COVER_H;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      resolve(null);
      return;
    }

    ctx.fillStyle = backgroundColorHex;
    ctx.fillRect(0, 0, COVER_W, COVER_H);

    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      let width = img.naturalWidth || 400;
      let height = img.naturalHeight || 300;
      const ratio = width / height;

      if (width > MAX_LOGO_W) {
        width = MAX_LOGO_W;
        height = width / ratio;
      }

      if (height > MAX_LOGO_H) {
        height = MAX_LOGO_H;
        width = height * ratio;
      }

      ctx.drawImage(
        img,
        (COVER_W - width) / 2,
        (COVER_H - height) / 2,
        width,
        height,
      );

      try {
        resolve(canvas.toDataURL('image/png'));
      } catch {
        resolve(null);
      }
    };

    img.onerror = () => resolve(null);
    img.src = logoUrl;
  });
}

export function toOnboardingProfileData(
  response: ProfileEnrichmentResponse,
): OnboardingProfileData {
  return {
    person: response.person,
    company: response.company,
  };
}

export function mergeCompanySelection(
  profileData: OnboardingProfileData,
  position: OnboardingPositionChoice,
  response: CompanyEnrichmentResponse,
): OnboardingProfileData {
  return {
    person: {
      ...profileData.person,
      currentPosition: {
        title: position.title,
        companyName: position.companyName,
        companyLinkedInUrl: position.companyLinkedInUrl,
      },
    },
    company: response.company ?? {
      name: position.companyName,
      websiteUrl: null,
      linkedInUrl: position.companyLinkedInUrl,
    },
  };
}

export async function toBrandingResult(
  branding: OnboardingBranding | null,
): Promise<BrandingResult | null> {
  if (!branding) return null;

  let coverImage: IFile | null = null;

  if (branding.logoUrl) {
    const dataUrl = await generateCoverDataUrl(
      branding.logoUrl,
      branding.backgroundColorHex,
    );

    if (dataUrl) {
      coverImage = buildSyntheticImageFile('brand-cover', dataUrl);
    }
  }

  if (!coverImage) {
    coverImage = buildSyntheticImageFile(
      'brand-background',
      await new Promise<string>((resolve) => {
        const canvas = document.createElement('canvas');
        canvas.width = COVER_W;
        canvas.height = COVER_H;
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          resolve('');
          return;
        }

        ctx.fillStyle = branding.backgroundColorHex;
        ctx.fillRect(0, 0, COVER_W, COVER_H);
        resolve(canvas.toDataURL('image/png'));
      }),
    );
  }

  return {
    coverImage,
    accentColorHex: branding.accentColorHex,
    buttonTextColor: branding.buttonTextColorHex,
    domain: branding.domain,
    logoUrl: branding.logoUrl,
  };
}

// ---------------------------------------------------------------------------
// Claim endpoint
// ---------------------------------------------------------------------------

/**
 * Wire-only settings for POST /api/onboarding/claim.
 * Derived from IProfileSettings but without `logo` (FE-only preview field)
 * and with `logo_file_id` made optional (set only when upload succeeded).
 */
export type ClaimOnboardingSettings = Omit<IProfileSettings, 'logo' | 'logo_file_id'> & {
  logo_file_id?: number;
};

/**
 * Wire payload for POST /api/onboarding/claim.
 * Derived from WorkingProfileForm but with:
 * - `photo` (FE-only IFile preview) replaced by `photo_file_id` (uploaded file ID)
 * - `settings` replaced by ClaimOnboardingSettings (no `logo: IFile`, wire-only)
 */
export type ClaimOnboardingProfile = Omit<WorkingProfileForm, 'photo' | 'settings'> & {
  photo_file_id: number | null;
  settings: ClaimOnboardingSettings;
};

export interface ClaimOnboardingResponse {
  profile_hash: string;
}

export async function claimOnboarding(
  previewToken: string,
  profile: ClaimOnboardingProfile,
): Promise<ClaimOnboardingResponse> {
  const { data } = await axiosApiService.post<ClaimOnboardingResponse>(
    `${BASE_URL}/api/onboarding/claim`,
    { preview_token: previewToken, profile },
  );
  return data;
}

// ---------------------------------------------------------------------------
// Post-auth asset upload helpers
// ---------------------------------------------------------------------------

/**
 * Converts a data URL string to a File object suitable for upload.
 */
function dataUrlToFile(dataUrl: string, filename: string): File {
  const [header, base64] = dataUrl.split(',');
  const mime = header.match(/:(.*?);/)?.[1] ?? 'image/png';
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new File([bytes], filename, { type: mime });
}

/**
 * Attempts to fetch a remote image URL and return it as a File.
 * Returns null if the fetch fails (e.g. CORS restriction).
 */
async function remoteUrlToFile(
  url: string,
  filename: string,
): Promise<File | null> {
  try {
    const res = await axios.get<Blob>(url, { responseType: 'blob' });
    return new File([res.data], filename, { type: res.data.type || 'image/jpeg' });
  } catch {
    return null;
  }
}

export interface UploadedAssets {
  photoFileId: number | null;
  logoFileId: number | null;
}

/**
 * After authentication, uploads the preview avatar and cover image as real
 * files so they can be persisted through the claim endpoint.
 *
 * - Cover image (composed data URL) is uploaded first and is reliable.
 * - Avatar (remote LinkedIn URL) is best-effort — CORS may prevent it.
 *
 * Both uploads are independent; failure of one does not block the other.
 */
export async function uploadPreviewAssets(
  orgID: number,
  profileFormData: WorkingProfileForm,
): Promise<UploadedAssets> {
  const [photoFileId, logoFileId] = await Promise.all([
    // ── Avatar (best-effort remote fetch) ──────────────────────────────────
    (async (): Promise<number | null> => {
      const photoUrl = profileFormData.photo?.original_url;
      if (!photoUrl || photoUrl.startsWith('data:')) {
        // data URLs for avatars are not currently produced; skip
        return null;
      }
      try {
        const file = await remoteUrlToFile(photoUrl, 'avatar.jpg');
        if (!file) return null;
        const res = await filesAPI.createAndUploadFile(orgID, file);
        return res.data.data.id;
      } catch {
        return null;
      }
    })(),

    // ── Cover image (composed data URL — reliable) ─────────────────────────
    (async (): Promise<number | null> => {
      const coverUrl = profileFormData.settings?.logo?.original_url;
      if (!coverUrl?.startsWith('data:')) return null;
      try {
        const file = dataUrlToFile(coverUrl, 'brand-cover.png');
        const res = await filesAPI.createAndUploadFile(orgID, file);
        return res.data.data.id;
      } catch {
        return null;
      }
    })(),
  ]);

  return { photoFileId, logoFileId };
}

/**
 * Builds a ClaimOnboardingProfile from a WorkingProfileForm plus the uploaded
 * real file IDs (which override the synthetic preview IDs).
 */
export function buildClaimProfile(
  formData: WorkingProfileForm,
  assets: UploadedAssets,
): ClaimOnboardingProfile {
  const { photo: _photo, settings, profile_file_links, ...rest } = formData;

  return {
    ...rest,
    photo_file_id: assets.photoFileId,
    profile_file_links: profile_file_links
      .filter(link => link.status === 'complete')
      .map(({ status: _s, ...link }) => link) as WorkingProfileForm['profile_file_links'],
    settings: {
      bg_color: settings.bg_color,
      text_color: settings.text_color,
      button_bg_color: settings.button_bg_color,
      button_text_color: settings.button_text_color,
      ...(assets.logoFileId !== null && { logo_file_id: assets.logoFileId }),
    },
  };
}
